const BASE_URL = process.env.REACT_APP_BASE_URL;
export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem("accessToken", accessToken);
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }
};

export const getAccessToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");

export const clearTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("isModerator");
};

let refreshPromise = null;

export const refreshAccessToken = async (refreshToken = getRefreshToken()) => {
  if (!refreshToken) {
    return null;
  }

  // A rotated refresh token can only be used once. Reuse the same request when
  // several protected API calls notice an expired access token simultaneously.
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/user/token/refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }

      const data = await response.json();

      setTokens(data.access, data.refresh || refreshToken);

      return data.access;
    } catch (error) {
      console.error("Error refreshing access token:", error);
      clearTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expirationTime = payload.exp * 1000;
    // Refresh shortly before expiration so the token cannot expire in transit.
    return Date.now() >= expirationTime - 30000;
  } catch (error) {
    console.error("Invalid token:", error);
    return true;
  }
};

export const authenticatedFetch = async (url, options = {}) => {
  let accessToken = getAccessToken();

  if (!accessToken || isTokenExpired(accessToken)) {
    accessToken = await refreshAccessToken();
  }

  const sendRequest = (token) => {
    const headers = new Headers(options.headers || {});

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return fetch(url, { ...options, headers });
  };

  let response = await sendRequest(accessToken);

  // The backend can reject a token before its local expiry (revocation, clock
  // skew, etc.). Refresh and retry the original request exactly once.
  if (response.status === 401 && getRefreshToken()) {
    accessToken = await refreshAccessToken();
    if (accessToken) {
      response = await sendRequest(accessToken);
    }
  }

  return response;
};
